/**
 * Per-region playback state machine.
 *
 * Modes (from the editor plan):
 *   skip          — fade-and-jump to end, exit immediately
 *   include       — play through (visual marker only)
 *   repeat        — replay region N times (cycleCounter), then exit
 *   reverse       — playbackRate = -|speed| from end → start, then continue past end
 *   fwd-back-fwd  — forward → reverse → forward (3 phases tracked by cycleCounter 0/1/2)
 *
 * Functions accept a context object so the player class doesn't share globals.
 *
 * @typedef {Object} StateMachineCtx
 * @property {HTMLVideoElement} video
 * @property {(target:number) => void} fadeAndSkip   bound from core/fade.js
 * @property {() => void} renderStrip                callback to refresh ui/strip.js
 * @property {{ activeRegion: any, cycleCounter: number, preEnterRate: number }} state
 */

export function enterRegion(ctx, r) {
  const { video, fadeAndSkip, renderStrip, state } = ctx;
  state.activeRegion = r;
  state.cycleCounter = 0;
  state.preEnterRate = video.playbackRate;

  if (r.mode === 'skip') {
    fadeAndSkip(r.end);
    exitRegion(ctx);
    return;
  }
  if (r.speed && Math.abs(r.speed - 1) > 0.001) {
    video.playbackRate = r.speed;
  }
  if (r.mode === 'reverse') {
    video.playbackRate = -Math.abs(r.speed || 1);
  } else if (r.mode === 'fwd-back-fwd') {
    video.playbackRate = Math.abs(r.speed || 1);
  }
  renderStrip();
}

export function exitRegion(ctx) {
  const { video, renderStrip, state } = ctx;
  video.playbackRate = state.preEnterRate;
  state.activeRegion = null;
  state.cycleCounter = 0;
  renderStrip();
}

export function handleActiveRegion(ctx, t) {
  const { video, state } = ctx;
  const r = state.activeRegion;
  if (!r) return;

  if (r.mode === 'repeat') {
    if (t >= r.end - 0.05) {
      state.cycleCounter++;
      if (state.cycleCounter < (r.repeat_count || 1)) {
        try { video.currentTime = r.start; } catch { /* noop */ }
      } else {
        exitRegion(ctx);
      }
    }
  } else if (r.mode === 'reverse') {
    if (t <= r.start + 0.05) {
      video.playbackRate = state.preEnterRate;
      try { video.currentTime = r.end + 0.05; } catch { /* noop */ }
      exitRegion(ctx);
    }
  } else if (r.mode === 'fwd-back-fwd') {
    if (state.cycleCounter === 0 && t >= r.end - 0.05) {
      video.playbackRate = -Math.abs(r.speed || 1);
      state.cycleCounter = 1;
    } else if (state.cycleCounter === 1 && t <= r.start + 0.05) {
      video.playbackRate = Math.abs(r.speed || 1);
      state.cycleCounter = 2;
    } else if (state.cycleCounter === 2 && t >= r.end - 0.05) {
      exitRegion(ctx);
    }
  } else if (r.mode === 'include') {
    if (t >= r.end - 0.05) exitRegion(ctx);
  }
}
